import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  Building2,
  Clock,
  Info,
  MonitorPlay,
  Search,
  Users as UsersIcon,
} from 'lucide-react';
import {
  api,
  type AppStreamUsageByOrganization,
  type AppStreamUsageByUser,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { cn } from '../lib/utils';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Pagination } from '../components/ui/Pagination';
import { SortableHeader } from '../components/ui/SortableHeader';
import { useSort } from '../lib/sort';
import { formatDateTime, formatHours, formatNumber, formatSeconds } from '../lib/format';

const PAGE_SIZE = 25;

const RANGES = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

const getOrgValue = (item: AppStreamUsageByOrganization, key: string): unknown => {
  switch (key) {
    case 'organizationName':
      return item.organizationName;
    case 'users':
      return item.users;
    case 'sessions':
      return item.sessions;
    case 'seconds':
      return item.seconds;
    case 'abandoned':
      return item.abandoned;
    default:
      return undefined;
  }
};

const getUserValue = (item: AppStreamUsageByUser, key: string): unknown => {
  switch (key) {
    case 'userId':
      return item.userId;
    case 'organization':
      return item.organizationName ?? '';
    case 'sessions':
      return item.sessions;
    case 'seconds':
      return item.seconds;
    case 'abandoned':
      return item.abandoned;
    case 'lastSeen':
      return item.lastSeen;
    default:
      return undefined;
  }
};

export function AppStreamUsage() {
  const [days, setDays] = useState(30);
  const [q, setQ] = useState('');
  const [orgPage, setOrgPage] = useState(1);
  const [userPage, setUserPage] = useState(1);

  const usage = useQuery({
    queryKey: ['appstream', 'usage', days],
    queryFn: () => api.getAppStreamUsage(days),
    staleTime: 10 * 60 * 1000,
  });

  const totals = usage.data?.totals;
  const chartData = useMemo(
    () =>
      (usage.data?.daily ?? []).map((d) => ({
        date: new Date(`${d.date}T00:00:00Z`).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        }),
        connected: d.sessions - d.abandoned,
        abandoned: d.abandoned,
        hours: Math.round((d.seconds / 3600) * 10) / 10,
      })),
    [usage.data],
  );

  const orgs = useMemo(() => usage.data?.byOrganization ?? [], [usage.data]);
  const filteredOrgs = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return orgs;
    return orgs.filter((o) => o.organizationName.toLowerCase().includes(needle));
  }, [orgs, q]);

  const users = useMemo(() => usage.data?.byUser ?? [], [usage.data]);
  const filteredUsers = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(
      (u) =>
        u.userId.toLowerCase().includes(needle) ||
        (u.organizationName ?? '').toLowerCase().includes(needle),
    );
  }, [users, q]);

  const orgSort = useSort(filteredOrgs, getOrgValue, { key: 'seconds', direction: 'desc' });
  const userSort = useSort(filteredUsers, getUserValue, { key: 'seconds', direction: 'desc' });

  // Clamp rather than reset: narrowing the search while on page 4 should land
  // on the last page that still has rows, not silently show an empty table.
  const orgPageCount = Math.max(1, Math.ceil(orgSort.sortedItems.length / PAGE_SIZE));
  const currentOrgPage = Math.min(orgPage, orgPageCount);
  const orgRows = useMemo(
    () => orgSort.sortedItems.slice((currentOrgPage - 1) * PAGE_SIZE, currentOrgPage * PAGE_SIZE),
    [orgSort.sortedItems, currentOrgPage],
  );

  const userPageCount = Math.max(1, Math.ceil(userSort.sortedItems.length / PAGE_SIZE));
  const currentUserPage = Math.min(userPage, userPageCount);
  const userRows = useMemo(
    () => userSort.sortedItems.slice((currentUserPage - 1) * PAGE_SIZE, currentUserPage * PAGE_SIZE),
    [userSort.sortedItems, currentUserPage],
  );

  const missingDays = usage.data?.missingDays ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Streaming usage</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Who consumed WorkSpaces Applications capacity, from AppStream's daily session reports.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(days)}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-40"
            aria-label="Date range"
          >
            {RANGES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {usage.error && <p className="text-sm text-destructive">{(usage.error as Error).message}</p>}

      {usage.data && !usage.data.enabled && (
        <Card>
          <CardContent className="flex items-start gap-2 pt-4 text-sm text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            No AppStream usage-report subscription is configured, so there is no history to read.
            Point one at an S3 bucket and set <code className="font-mono text-xs">appstream.usageReportBucket</code>{' '}
            in <code className="font-mono text-xs">cdk/cdk.json</code>.
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Sessions"
          icon={<MonitorPlay className="h-4 w-4 text-muted-foreground" />}
          loading={usage.isLoading}
        >
          <div className="text-2xl font-bold tabular-nums">{formatNumber(totals?.sessions)}</div>
          <p className="text-xs text-muted-foreground">ended in the last {days} days</p>
        </StatCard>
        <StatCard
          title="Unique users"
          icon={<UsersIcon className="h-4 w-4 text-muted-foreground" />}
          loading={usage.isLoading}
        >
          <div className="text-2xl font-bold tabular-nums">{formatNumber(totals?.users)}</div>
          <p className="text-xs text-muted-foreground">
            across {formatNumber(orgs.length)} organizations
          </p>
        </StatCard>
        <StatCard
          title="Streaming time"
          icon={<Clock className="h-4 w-4 text-muted-foreground" />}
          loading={usage.isLoading}
        >
          <div className="text-2xl font-bold tabular-nums">{formatHours(totals?.seconds)}</div>
          <p className="text-xs text-muted-foreground">
            longest session {formatSeconds(totals?.longestSeconds)}
          </p>
        </StatCard>
        <StatCard
          title="Never connected"
          icon={<AlertTriangle className="h-4 w-4 text-muted-foreground" />}
          loading={usage.isLoading}
        >
          <div className="text-2xl font-bold tabular-nums">{formatNumber(totals?.abandoned)}</div>
          <p className="text-xs text-muted-foreground">
            {totals && totals.sessions > 0
              ? `${Math.round((totals.abandoned / totals.sessions) * 100)}% of sessions held a slot unused`
              : 'sessions that held a slot unused'}
          </p>
        </StatCard>
      </div>

      {missingDays.length > 0 && (
        <p className="flex items-start gap-2 text-sm text-caution">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {missingDays.length === 1
            ? `No usage report was found for ${missingDays[0]}.`
            : `No usage report was found for ${missingDays.length} of the ${days} days in range.`}{' '}
          Those days read as zero rather than as a gap.
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Sessions per day</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={16}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip cursor={{ fill: 'var(--color-muted)' }} contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="connected" stackId="a" name="Connected" fill="var(--color-ok)" />
                  <Bar
                    dataKey="abandoned"
                    stackId="a"
                    name="Never connected"
                    fill="var(--color-caution)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Streaming hours per day</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={16}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip cursor={{ fill: 'var(--color-muted)' }} contentStyle={tooltipStyle} />
                  <Bar dataKey="hours" name="Hours" fill="var(--color-live)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="relative w-72">
        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOrgPage(1);
            setUserPage(1);
          }}
          placeholder="Filter organizations and users…"
          className="pl-9"
        />
      </div>

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            By organization
          </CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-y border-border bg-muted/50">
              <tr>
                <SortableHeader
                  label="Organization"
                  sortKey="organizationName"
                  sort={orgSort.sort}
                  onToggle={orgSort.toggle}
                />
                <SortableHeader
                  label="Users"
                  sortKey="users"
                  sort={orgSort.sort}
                  onToggle={orgSort.toggle}
                  align="right"
                />
                <SortableHeader
                  label="Sessions"
                  sortKey="sessions"
                  sort={orgSort.sort}
                  onToggle={orgSort.toggle}
                  align="right"
                />
                <SortableHeader
                  label="Streaming time"
                  sortKey="seconds"
                  sort={orgSort.sort}
                  onToggle={orgSort.toggle}
                  align="right"
                />
                <SortableHeader
                  label="Never connected"
                  sortKey="abandoned"
                  sort={orgSort.sort}
                  onToggle={orgSort.toggle}
                  align="right"
                />
              </tr>
            </thead>
            <tbody>
              {usage.isLoading && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {orgRows.map((o) => (
                <tr
                  key={o.organizationId ?? o.organizationName}
                  className={cn(
                    'border-b border-border transition-colors last:border-0 hover:bg-muted/50',
                    o.internal && 'bg-accent-soft/40 shadow-[inset_2px_0_0_var(--color-accent)]',
                  )}
                >
                  <td className="px-4 py-3">
                    {o.organizationId ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="font-medium">{o.organizationName}</span>
                        {o.internal && (
                          <Badge variant="outline" className="border-accent-line text-accent">
                            Internal
                          </Badge>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground italic">{o.organizationName}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatNumber(o.users)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatNumber(o.sessions)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatHours(o.seconds)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {o.abandoned > 0 ? (
                      <Badge variant="warning">{formatNumber(o.abandoned)}</Badge>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </td>
                </tr>
              ))}
              {!usage.isLoading && orgRows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                    No usage in this range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!usage.isLoading && orgSort.sortedItems.length > 0 && (
          <Pagination
            page={currentOrgPage}
            pageCount={orgPageCount}
            pageSize={PAGE_SIZE}
            total={orgSort.sortedItems.length}
            onPageChange={setOrgPage}
          />
        )}
      </Card>

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <UsersIcon className="h-4 w-4 text-muted-foreground" />
            By user
          </CardTitle>
          {usage.data?.topUsers !== undefined && users.length >= usage.data.topUsers && (
            <p className="text-xs text-muted-foreground">
              Top {usage.data.topUsers} by streaming time. Organization totals above cover everyone.
            </p>
          )}
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-y border-border bg-muted/50">
              <tr>
                <SortableHeader
                  label="User"
                  sortKey="userId"
                  sort={userSort.sort}
                  onToggle={userSort.toggle}
                />
                <SortableHeader
                  label="Organization"
                  sortKey="organization"
                  sort={userSort.sort}
                  onToggle={userSort.toggle}
                />
                <SortableHeader
                  label="Sessions"
                  sortKey="sessions"
                  sort={userSort.sort}
                  onToggle={userSort.toggle}
                  align="right"
                />
                <SortableHeader
                  label="Streaming time"
                  sortKey="seconds"
                  sort={userSort.sort}
                  onToggle={userSort.toggle}
                  align="right"
                />
                <SortableHeader
                  label="Never connected"
                  sortKey="abandoned"
                  sort={userSort.sort}
                  onToggle={userSort.toggle}
                  align="right"
                />
                <SortableHeader
                  label="Last session"
                  sortKey="lastSeen"
                  sort={userSort.sort}
                  onToggle={userSort.toggle}
                  align="right"
                />
              </tr>
            </thead>
            <tbody>
              {usage.isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {userRows.map((u) => (
                <tr
                  key={u.userId}
                  className={cn(
                    'border-b border-border transition-colors last:border-0 hover:bg-muted/50',
                    u.internal && 'bg-accent-soft/40 shadow-[inset_2px_0_0_var(--color-accent)]',
                  )}
                >
                  <td className="px-4 py-3 font-medium">{u.userId}</td>
                  <td className="px-4 py-3">
                    {u.organizationName ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-fg-2">{u.organizationName}</span>
                        {u.internal && (
                          <Badge variant="outline" className="border-accent-line text-accent">
                            Internal
                          </Badge>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground italic">Unattributed</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatNumber(u.sessions)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatHours(u.seconds)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                    {formatNumber(u.abandoned)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-muted-foreground">
                    {formatDateTime(u.lastSeen)}
                  </td>
                </tr>
              ))}
              {!usage.isLoading && userRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    No usage in this range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!usage.isLoading && userSort.sortedItems.length > 0 && (
          <Pagination
            page={currentUserPage}
            pageCount={userPageCount}
            pageSize={PAGE_SIZE}
            total={userSort.sortedItems.length}
            onPageChange={setUserPage}
          />
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Info className="h-4 w-4 text-muted-foreground" />
            About this data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            AppStream writes one report per day covering the sessions that{' '}
            <span className="text-foreground">ended</span> that day, so a session running across
            midnight is counted on its closing date. The current day is not included — it has not
            been written yet.
          </p>
          <p>
            <span className="text-foreground">Never connected</span> counts sessions that reserved a
            fleet instance without anyone ever streaming to it. A persistent run of them on one
            organization usually means a launcher retry loop, not real demand.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

const tooltipStyle = {
  background: 'var(--color-card)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  fontSize: 12,
  color: 'var(--color-foreground)',
};

function StatCard({
  title,
  icon,
  children,
  loading,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        {loading ? <span className="text-muted-foreground">…</span> : children}
      </CardContent>
    </Card>
  );
}
